import type { Client } from '@suntex.fim/api-client';

const CERT_PDF_BASE_URL =
  'https://cdn.firstinmath.com/content/pdf/certificates';

/** Only allow alphanumeric, hyphens, underscores, and a single forward slash. */
const VALID_CERT_LINK = /^[\w-]+\/[\w-]+$/;

/**
 * Downloads a certificate PDF template from the CDN, fills it with the
 * player's certificate data from the API, and triggers a browser download.
 */
export async function downloadCertificate(
  api: Client,
  certLink: string,
  playerId: number,
): Promise<void> {
  if (!VALID_CERT_LINK.test(certLink)) {
    throw new Error(`Invalid certificate link: ${certLink}`);
  }

  const { PDFDocument } = await import('pdf-lib');

  const certData = await api.get_Assessment_CertificateData({
    playerId: playerId as Parameters<
      typeof api.get_Assessment_CertificateData
    >[0]['playerId'],
  });

  const url = `${CERT_PDF_BASE_URL}/${certLink}.pdf`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Certificate PDF not found: ${url}`);
  }

  const pdfBytes = await response.arrayBuffer();
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const form = pdfDoc.getForm();
  const formFields = form.getFields();

  // The API returns camelCase keys (studentName, teacherName, dateCompleted)
  // but the PDF form fields use PascalCase (StudentName, TeacherName, DateCompleted).
  const fieldData: Record<string, string> = {};
  for (const [key, value] of Object.entries({ ...certData })) {
    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
    fieldData[pascalKey] = value;
  }

  for (const [key, value] of Object.entries(fieldData)) {
    const matchedField = formFields.find(
      (f) => f.getName().toLowerCase() === key.toLowerCase(),
    );
    if (matchedField) {
      const field = form.getTextField(matchedField.getName());
      field.setText(value || '');
      field.enableReadOnly();
    }
  }

  const filledPdfBytes = await pdfDoc.save();
  const lastSegment = certLink.split('/').pop();
  const filename =
    lastSegment && lastSegment.length > 0 ? lastSegment : 'certificate';
  const pdfBuffer = new ArrayBuffer(filledPdfBytes.byteLength);
  new Uint8Array(pdfBuffer).set(filledPdfBytes);
  const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${filename}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}
